/**
 * @fileoverview Pure helpers for the job's additional places of business
 * (MOR-989). Shared by the Convex backend (search seeding, documents, the
 * conflict brief) and the React settings page, so it must stay free of
 * `ctx` side effects.
 */

/**
 * Structural shape of one place — the schema's `AdditionalPlaceOfBusiness`
 * satisfies it, and so do the lighter objects the precedent field mapper
 * accepts from the client, which is why this is not tied to the schema type.
 */
export interface PlaceOfBusinessFields {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  includeInDocuments: boolean;
}

type AddressParts = Pick<
  PlaceOfBusinessFields,
  "street" | "suburb" | "state" | "postcode"
>;

/**
 * Trim every component. Rows with neither a street nor a suburb are dropped
 * (there is nothing to search or print), and exact duplicates collapse so a
 * practitioner who re-enters the same premises twice does not double-seed a
 * land title search or double-print an address line.
 */
export function normaliseAdditionalPlacesOfBusiness<
  T extends PlaceOfBusinessFields,
>(places: ReadonlyArray<T>): T[] {
  const seen = new Set<string>();
  return places.flatMap((place) => {
    const trimmed: T = {
      ...place,
      street: place.street.trim(),
      suburb: place.suburb.trim(),
      state: place.state.trim().toUpperCase(),
      postcode: place.postcode.trim(),
    };
    if (!trimmed.street && !trimmed.suburb) return [];
    const key = additionalPlaceOfBusinessKey(trimmed);
    if (seen.has(key)) return [];
    seen.add(key);
    return [trimmed];
  });
}

/**
 * Case- and whitespace-insensitive identity for an address, used to de-dupe
 * rows against each other and against land title searches already queued.
 */
export function additionalPlaceOfBusinessKey(place: AddressParts): string {
  return [place.state, place.street, place.suburb, place.postcode]
    .map(normaliseAddressPart)
    .join("|");
}

/** "12 Industrial Rd, Wetherill Park NSW 2164" — one line for documents. */
export function formatAdditionalPlaceOfBusiness(place: AddressParts): string {
  const tail = [place.suburb, place.state, place.postcode]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
  return [place.street.trim(), tail].filter(Boolean).join(", ");
}

/**
 * Document-bound places, formatted one per line. Returns `undefined` when
 * nothing is selected so callers can omit the field entirely.
 */
export function additionalPlaceOfBusinessLines(
  places: ReadonlyArray<PlaceOfBusinessFields> | null | undefined,
): string | undefined {
  const lines = normaliseAdditionalPlacesOfBusiness(places ?? [])
    .filter((place) => place.includeInDocuments)
    .map(formatAdditionalPlaceOfBusiness)
    .filter(Boolean);
  return lines.length > 0 ? lines.join("\n") : undefined;
}

function normaliseAddressPart(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[^\w\s]/g, "")
    .trim();
}
